/**
 * TaskDrawerDetails Component
 *
 * Renders the details/metadata card in the drawer sidebar including:
 * - Urgency with color indicator
 * - Start date and due date
 * - Created date and requestor
 * - Channel partner and advertiser
 * - Request type and subtype
 * - External link (if available)
 *
 * Inline editing (PLATFORM users): Urgency, Start Date, Due Date, Channel Partner,
 * Advertiser, Request Type, Request Details. Requested By and Created stay read-only.
 *
 * Accessibility:
 * - Semantic definition list for metadata
 * - ARIA labels for dates and status indicators
 * - Color-blind friendly urgency indicators (includes text label)
 *
 * @see WCAG 2.1 AA - 1.4.1 Use of Color
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card, DatePicker, Select, Tooltip } from 'antd';
import {
  EditOutlined,
  LinkOutlined,
  LoadingOutlined,
  RightOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';
import {
  subTypes,
  taskTypes,
  urgencyHexColors,
  urgencyTypes,
} from '../../configs/task.config';
import '../../styles/index.scss';

const { Option } = Select;

/**
 * Format a date string for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date or '-'
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Get relative time string
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time or empty
 */
const getRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return '';
};

const TaskDrawerDetails = ({
  currentTask,
  level,
  updateTaskLoading,
  isEditingDetails,
  setIsEditingDetails,
  detailsForm,
  onDetailsFieldChange,
  onDetailsSave,
  onDetailsCancel,
  channelPartnerOrgs,
  advertiserOrgs,
  channelPartnerOrgsLoading,
  advertiserOrgsLoading,
  fetchChannelPartnerOrgs,
  fetchAdvertiserOrgs,
}) => {
  const handleLinkClick = () => {
    const url = currentTask.linkUrl;
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const createdRelative = getRelativeTime(currentTask.createdAt);

  const availableSubTypes = detailsForm && detailsForm.requestType
    ? subTypes[detailsForm.requestType] || []
    : [];

  // Mirrors the rules in TaskBasicFields: due date required only when not linked to
  // a project; start must be strictly before due when both are set.
  const isDueDateMissing = !currentTask.project && !detailsForm.dueDate;
  const isStartAfterOrEqualDue =
    !!detailsForm.startDate &&
    !!detailsForm.dueDate &&
    !detailsForm.startDate.isBefore(detailsForm.dueDate, 'day');
  const isSaveDisabled =
    updateTaskLoading ||
    !detailsForm.channelPartnerId ||
    !detailsForm.advertiserId ||
    isDueDateMissing ||
    isStartAfterOrEqualDue;

  // On Edit open, prefetch both lists in parallel. Safe here because each fetcher
  // owns its own state bucket (no shared routing flag) — races between them are
  // impossible by construction.
  const onEditClick = () => {
    setIsEditingDetails(true);
    fetchChannelPartnerOrgs();
    if (currentTask.channelPartner && currentTask.channelPartner.id) {
      fetchAdvertiserOrgs('', currentTask.channelPartner.id);
    }
  };

  return (
    <Card
      size="small"
      title={
        <div className="task-drawer-full__card-header">
          <span id="details-heading" className="task-drawer-full__card-title">
            Details
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {currentTask.linkUrl && (
              <Tooltip title="Open external link">
                <LinkOutlined
                  className="task-drawer-full__edit-icon"
                  onClick={handleLinkClick}
                  role="button"
                  aria-label="Open external link in new tab"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleLinkClick();
                    }
                  }}
                />
              </Tooltip>
            )}
            {!isEditingDetails && level === 'PLATFORM' && (
              <Tooltip title="Edit details">
                <EditOutlined
                  className="task-drawer-full__edit-icon"
                  onClick={onEditClick}
                  role="button"
                  aria-label="Edit task details"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onEditClick();
                    }
                  }}
                />
              </Tooltip>
            )}
          </div>
        </div>
      }
      className="task-drawer-full__card"
      role="region"
      aria-labelledby="details-heading"
    >
      {/* Grid Layout for Details */}
      <div className="task-drawer-full__details-grid" role="list" aria-label="Task details">
        {/* Urgency */}
        <div className="task-drawer-full__details-item" role="listitem">
          <Text
            type="secondary"
            className="task-drawer-full__detail-label"
            id="urgency-label"
          >
            Urgency
          </Text>
          {isEditingDetails ? (
            <Select
              size="small"
              style={{ width: '100%' }}
              value={detailsForm.urgency || undefined}
              placeholder="Select Urgency"
              allowClear
              onChange={(value) => onDetailsFieldChange('urgency', value || null)}
            >
              {Object.entries(urgencyTypes).map(([label, value]) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          ) : currentTask.urgency ? (
            <span
              className={`task-drawer-full__urgency-chip${currentTask.urgency === 'CRITICAL' ? ' task-drawer-full__urgency-chip--critical' : ''}`}
              style={{ '--badge-color': urgencyHexColors[currentTask.urgency] }}
              aria-labelledby="urgency-label"
            >
              <span
                className="task-drawer-full__status-dot"
                aria-hidden="true"
              />
              {startCase(currentTask.urgency.toLowerCase())}
            </span>
          ) : (
            <Text className="task-drawer-full__detail-value--muted">-</Text>
          )}
        </div>

        {/* Due Date (required when not linked to a project) */}
        <div className="task-drawer-full__details-item" role="listitem">
          <Text type="secondary" className="task-drawer-full__detail-label">
            Due Date
            {isEditingDetails && !currentTask.project && (
              <span style={{ color: '#ff4d4f' }}> *</span>
            )}
          </Text>
          {isEditingDetails ? (
            <DatePicker
              size="small"
              style={{ width: '100%' }}
              value={detailsForm.dueDate}
              onChange={(value) => onDetailsFieldChange('dueDate', value)}
            />
          ) : (
            <Text
              className="task-drawer-full__detail-value"
              aria-label={`Due date: ${formatDate(currentTask.dueDate)}${currentTask.recurring ? ', recurring task' : ''}`}
            >
              {formatDate(currentTask.dueDate)}
              {currentTask.recurring && (
                <Tooltip title="Recurring task">
                  <SyncOutlined
                    className="task-drawer-full__icon-suffix"
                    aria-label="Recurring task"
                  />
                </Tooltip>
              )}
            </Text>
          )}
        </div>

        {/* Start Date */}
        <div className="task-drawer-full__details-item" role="listitem">
          <Text type="secondary" className="task-drawer-full__detail-label">
            Start Date
          </Text>
          {isEditingDetails ? (
            <DatePicker
              size="small"
              style={{ width: '100%' }}
              value={detailsForm.startDate}
              onChange={(value) => onDetailsFieldChange('startDate', value)}
            />
          ) : (
            <Text
              className="task-drawer-full__detail-value"
              aria-label={`Start date: ${formatDate(currentTask.startDate)}`}
            >
              {formatDate(currentTask.startDate)}
            </Text>
          )}
        </div>

        {/* Created On (read-only) */}
        <div className="task-drawer-full__details-item" role="listitem">
          <Text type="secondary" className="task-drawer-full__detail-label">
            Created
          </Text>
          <Text className="task-drawer-full__detail-value">
            {formatDate(currentTask.createdAt)}
            {createdRelative && (
              <span className="task-drawer-full__detail-relative">
                ({createdRelative})
              </span>
            )}
          </Text>
        </div>

        {/* Divider */}
        <div className="task-drawer-full__details-divider" aria-hidden="true" />

        {/* Requested By (read-only) */}
        <div className="task-drawer-full__details-item task-drawer-full__details-item--full-width" role="listitem">
          <Text type="secondary" className="task-drawer-full__detail-label">
            Requested By
          </Text>
          <Text className="task-drawer-full__detail-value">
            {startCase(
              currentTask.createdBy?.name ||
              currentTask.createdBy?.username ||
              'Unknown'
            )}
          </Text>
        </div>

        {/* Request Type */}
        <div className="task-drawer-full__details-item task-drawer-full__details-item--full-width" role="listitem">
          <Text type="secondary" className="task-drawer-full__detail-label">
            Request Type
          </Text>
          {isEditingDetails ? (
            <Select
              size="small"
              style={{ width: '100%' }}
              value={detailsForm.requestType || undefined}
              placeholder="Select Request Type"
              allowClear
              showSearch
              onChange={(value) => onDetailsFieldChange('requestType', value || null)}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {taskTypes.map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          ) : currentTask.requestType ? (
            <div className="task-drawer-full__request-type">
              <span>{currentTask.requestType}</span>
              {currentTask.requestSubType && (
                <>
                  <RightOutlined className="task-drawer-full__request-arrow" />
                  <span className="task-drawer-full__request-subtype">
                    {currentTask.requestSubType}
                  </span>
                </>
              )}
            </div>
          ) : (
            <Text className="task-drawer-full__detail-value--muted">-</Text>
          )}
        </div>

        {/* Request Details (sub-type) — only shown when editing, since read-only mode
            already renders the sub-type inline with Request Type above. */}
        {isEditingDetails && (
          <div className="task-drawer-full__details-item task-drawer-full__details-item--full-width" role="listitem">
            <Text type="secondary" className="task-drawer-full__detail-label">
              Request Details
            </Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={detailsForm.requestSubType || undefined}
              placeholder={
                availableSubTypes.length > 0
                  ? 'Select Sub Type'
                  : 'No sub-types for this request type'
              }
              allowClear
              disabled={availableSubTypes.length === 0}
              onChange={(value) => onDetailsFieldChange('requestSubType', value || null)}
            >
              {availableSubTypes.map((sub) => (
                <Option key={sub} value={sub}>
                  {sub}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {/* Channel Partner (required) */}
        {(isEditingDetails || currentTask.channelPartner) && (
          <div className="task-drawer-full__details-item task-drawer-full__details-item--full-width" role="listitem">
            <Text type="secondary" className="task-drawer-full__detail-label">
              Channel Partner{isEditingDetails && <span style={{ color: '#ff4d4f' }}> *</span>}
            </Text>
            {isEditingDetails ? (
              <Select
                size="small"
                style={{ width: '100%' }}
                value={detailsForm.channelPartnerId || undefined}
                placeholder="Select Channel Partner"
                showSearch
                loading={channelPartnerOrgsLoading}
                onFocus={() => fetchChannelPartnerOrgs()}
                onSearch={(value) => fetchChannelPartnerOrgs(value)}
                onChange={(value) => {
                  onDetailsFieldChange('channelPartnerId', value || null);
                  if (value) fetchAdvertiserOrgs('', value);
                }}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {channelPartnerOrgs.map((org) => (
                  <Option key={org.id} value={org.id}>
                    {org.name}
                  </Option>
                ))}
              </Select>
            ) : (
              <Text className="task-drawer-full__detail-value">
                {currentTask.channelPartner.name}
              </Text>
            )}
          </div>
        )}

        {/* Advertiser (required) */}
        {(isEditingDetails || currentTask.advertiser) && (
          <div className="task-drawer-full__details-item task-drawer-full__details-item--full-width" role="listitem">
            <Text type="secondary" className="task-drawer-full__detail-label">
              Advertiser{isEditingDetails && <span style={{ color: '#ff4d4f' }}> *</span>}
            </Text>
            {isEditingDetails ? (
              <Select
                size="small"
                style={{ width: '100%' }}
                value={detailsForm.advertiserId || undefined}
                placeholder={
                  detailsForm.channelPartnerId
                    ? 'Select Advertiser'
                    : 'Select channel partner first'
                }
                showSearch
                disabled={!detailsForm.channelPartnerId}
                loading={advertiserOrgsLoading}
                onFocus={() => fetchAdvertiserOrgs('', detailsForm.channelPartnerId)}
                onSearch={(value) => fetchAdvertiserOrgs(value, detailsForm.channelPartnerId)}
                onChange={(value) => onDetailsFieldChange('advertiserId', value || null)}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {advertiserOrgs.map((org) => (
                  <Option key={org.id} value={org.id}>
                    {org.name}
                  </Option>
                ))}
              </Select>
            ) : (
              <Text className="task-drawer-full__detail-value">
                {currentTask.advertiser.name}
              </Text>
            )}
          </div>
        )}
      </div>

      {isEditingDetails && isStartAfterOrEqualDue && (
        <Text type="danger" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          Start date must be before due date.
        </Text>
      )}

      {isEditingDetails && (
        <div className="task-drawer-full__edit-actions">
          <Button
            type="primary"
            className="task-drawer-full__save-btn"
            onClick={onDetailsSave}
            disabled={isSaveDisabled}
          >
            {updateTaskLoading && <LoadingOutlined />} Save
          </Button>
          <Button
            className="task-drawer-full__cancel-btn"
            onClick={onDetailsCancel}
            disabled={updateTaskLoading}
          >
            Cancel
          </Button>
        </div>
      )}
    </Card>
  );
};

TaskDrawerDetails.propTypes = {
  currentTask: PropTypes.shape({
    id: PropTypes.string,
    urgency: PropTypes.oneOf(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']),
    startDate: PropTypes.string,
    dueDate: PropTypes.string,
    createdAt: PropTypes.string,
    recurring: PropTypes.bool,
    linkUrl: PropTypes.string,
    requestType: PropTypes.string,
    requestSubType: PropTypes.string,
    createdBy: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      username: PropTypes.string,
    }),
    channelPartner: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    advertiser: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  }).isRequired,
  level: PropTypes.string,
  updateTaskLoading: PropTypes.bool,
  isEditingDetails: PropTypes.bool,
  setIsEditingDetails: PropTypes.func,
  detailsForm: PropTypes.shape({
    urgency: PropTypes.string,
    startDate: PropTypes.object,
    dueDate: PropTypes.object,
    requestType: PropTypes.string,
    requestSubType: PropTypes.string,
    channelPartnerId: PropTypes.string,
    advertiserId: PropTypes.string,
  }),
  onDetailsFieldChange: PropTypes.func,
  onDetailsSave: PropTypes.func,
  onDetailsCancel: PropTypes.func,
  channelPartnerOrgs: PropTypes.array,
  advertiserOrgs: PropTypes.array,
  channelPartnerOrgsLoading: PropTypes.bool,
  advertiserOrgsLoading: PropTypes.bool,
  fetchChannelPartnerOrgs: PropTypes.func,
  fetchAdvertiserOrgs: PropTypes.func,
};

TaskDrawerDetails.defaultProps = {
  level: null,
  updateTaskLoading: false,
  isEditingDetails: false,
  setIsEditingDetails: () => {},
  detailsForm: {
    urgency: null,
    startDate: null,
    dueDate: null,
    requestType: null,
    requestSubType: null,
    channelPartnerId: null,
    advertiserId: null,
  },
  onDetailsFieldChange: () => {},
  onDetailsSave: () => {},
  onDetailsCancel: () => {},
  channelPartnerOrgs: [],
  advertiserOrgs: [],
  channelPartnerOrgsLoading: false,
  advertiserOrgsLoading: false,
  fetchChannelPartnerOrgs: () => {},
  fetchAdvertiserOrgs: () => {},
};

export default TaskDrawerDetails;
